import React from 'react';
import { connect } from 'react-redux';
import { arrayMove } from 'react-sortable-hoc';
import promise from 'promise';
import PropTypes from 'prop-types';

import { Row } from '@zillow/zrs-comet';
import { FormHelp, Text, Spacer } from '@zillow/constellation';

import { ModalSelectors, UserSelectors } from 'app/shared/selectors';
import appLogger from 'app/shared/loggers/appLogger';
import DropzoneWrapper from 'app/shared/components/postingPath/DropzoneWrapper';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';
import ImageCarouselModal from 'app/shared/components/Modal/ImageCarouselModal';
import InvalidFiletypeText from 'app/shared/components/postingPath/InvalidFiletypeText';
import ModalActions from 'app/shared/actions/ModalActions';
import NotificationConstants from 'app/shared/constants/NotificationConstants';
import Photo from 'app/shared/model/Photo';
import photoUtils from 'app/shared/utils/photoUtils';
import PostingActions from 'app/shared/actions/PostingActions';
import stringHelper from 'app/shared/utils/stringHelper';
import ThumbList from 'app/shared/components/postingPath/ThumbList';

import { StyledHiddenAnchor } from 'app/shared/StyledComponents/StyledComponents';
import { StyledPhotoUpload, PhotoUploadDropzoneContainer, StyledCol } from './StyledComponents';


const MISMATCH_PHOTOS_ERROR = 'An error occurred. Please refresh the page and try again.';

class PhotoUpload extends React.Component {
    static propTypes = {
        hasError: PropTypes.bool,
        statusMessage: PropTypes.string,

        // Required
        formData: PropTypes.object.isRequired,
        isListingPhotoProcessing: PropTypes.bool.isRequired,
        errorObj: PropTypes.object.isRequired,
        updateStatusMessage: PropTypes.func.isRequired,
        updateListingPhotoProcessingStatus: PropTypes.func.isRequired,
        updateErrorObj: PropTypes.func.isRequired,
        updateFormData: PropTypes.func.isRequired,
        isInputFormDisabled: PropTypes.bool,
        showHeading: PropTypes.bool,
    };

    static contextTypes = {
        brand: PropTypes.string,
        paths: PropTypes.object,
    };

    static defaultProps = {
        hasError: false,
        statusMessage: '',
        isInputFormDisabled: false,
        showHeading: true,
    };

    constructor(props) {
        super(props);

        this.state = {
            currentSlideIndex: 0,
            photos: this.props.formData.photos || [],
            isListingPhotoProcessing: this.props.isListingPhotoProcessing || false,
            startSlideIndex: 0,
            statusMessage: this.props.statusMessage || '',
            uploadingImages: false,
            dropzoneStatusMessage: '',
            isfileSizeOutOfLimit: false,
            showInvalidFiletypeText: false,
        };

        // bind methods
        this.handleItemClick = this.handleItemClick.bind(this);
        this.displayImageCarouselModal = this.displayImageCarouselModal.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.onDropRejected = this.onDropRejected.bind(this);
        this.saveSortedPhotos = this.saveSortedPhotos.bind(this);
        this.removePhoto = this.removePhoto.bind(this);
        this.renderDropZone = this.renderDropZone.bind(this);
        this.renderUploadedPhotos = this.renderUploadedPhotos.bind(this);
        this.rotatePhoto = this.rotatePhoto.bind(this);
        this.savePhotoCaption = this.savePhotoCaption.bind(this);
        this.shouldCancelSortableListStart = this.shouldCancelSortableListStart.bind(this);
        this.updateCaption = this.updateCaption.bind(this);
        this.uploadPhoto = this.uploadPhoto.bind(this);
        this.onSortEnd = this.onSortEnd.bind(this);
        this.updateCurrentSlideIndex = this.updateCurrentSlideIndex.bind(this);
    }

    addAndDisplayCaptionError(photoId, errorMessage) {
        const { photoCaptions } = this.props.errorObj;
        const updatedCaptionErrorObj = new Map(photoCaptions);
        updatedCaptionErrorObj.set(photoId, errorMessage);
        this.props.updateErrorObj({ photoCaptions: updatedCaptionErrorObj });
    }

    deleteIfCaptionErrorExistsAndDisplayNextError(photoId) {
        const { photoCaptions } = this.props.errorObj;
        const updatedCaptionErrorObj = new Map(photoCaptions);
        updatedCaptionErrorObj.delete(photoId);

        // Display another error if we still have errors on other photos.
        if (updatedCaptionErrorObj.size > 0) {
            this.props.updateErrorObj({ photoCaptions: updatedCaptionErrorObj });
        } else {
            this.props.updateErrorObj({ photoCaptions: null });
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.isListingPhotoProcessing !== this.state.isListingPhotoProcessing) {
            this.setState({
                isListingPhotoProcessing: nextProps.isListingPhotoProcessing,
                photos: nextProps.formData.photos,
                statusMessage: nextProps.statusMessage,
            });
        }
    }

    displayImageCarouselModal() {
        this.props.dispatch(ModalActions.updateDisplayImageCarouselModal({ isOpen: true }));
    }

    handleItemClick(index) {
        if (!this.state.isListingPhotoProcessing) {
            this.setState({ currentSlideIndex: index, startSlideIndex: index });
            this.displayImageCarouselModal();
        }
    }

    updateCurrentSlideIndex(index) {
        this.setState({ currentSlideIndex: index });
    }

    handleInvalidExtension() {
        this.setState({
            uploadingImages: false,
            showInvalidFiletypeText: true,
        });

        this.props.updateStatusMessage(
            ErrorMessageConstants.DEFAULT_PHOTO_ERROR,
            NotificationConstants.ERROR
        );
    }

    onDrop(files) {
        const filesCount = files.length;
        let photoText = stringHelper.pluralize(filesCount, 'photo', 'photos');
        let uploadStatusText = `Uploading ${photoText}...`;

        // preload all images
        // return all files that successfully preloaded
        photoUtils.preloadImages(files).then((files) => {
            // if any files could not load
            if (filesCount !== files.length) {
                const numFilesWithErrors = filesCount - files.length;
                photoText = stringHelper.pluralize(files.length, 'photo', 'photos');

                uploadStatusText = `Uploading ${photoText} ${numFilesWithErrors} could not be uploaded...`;
            }

            const promiseArr = [];
            let counter = 1;
            let hasInvalidExtension = false;

            this.props.updateStatusMessage(uploadStatusText);
            this.props.updateListingPhotoProcessingStatus(true);

            this.setState({
                uploadingImages: true,
                dropzoneStatusMessage: uploadStatusText,
                showInvalidFiletypeText: false,
            });

            // do upload
            files.forEach((file) => {
                const isValidFiletype = photoUtils.hasValidExtension(file);

                if (isValidFiletype) {
                    const p = new Promise((resolve) => {
                        this.uploadPhoto(file)
                            .then((result) => {
                                if (!result) {
                                    this.setState({ uploadingImages: false });

                                    // resolve with error
                                    resolve({ error: true });
                                }

                                if (this.props.errorObj.photos && this.state.photos.length > 1) {
                                    this.props.updateErrorObj({ photos: null });
                                }

                                uploadStatusText = `Uploaded ${counter} out of ${photoText}!`;
                                this.props.updateStatusMessage(uploadStatusText);
                                this.setState({
                                    dropzoneStatusMessage: uploadStatusText,
                                });
                                counter += 1;
                                // resolve photo
                                resolve(new Photo(result));
                            });
                    });

                    promiseArr.push(p);
                } else {
                    hasInvalidExtension = true;
                }
            });

            // if there are promises
            if (promiseArr.length > 0) {
                // handle promises
                promise
                    .all(promiseArr)
                    .then((response) => {
                        let foundError = false;
                        // filter out error objects from response array
                        // appear when user uploads photos in offline / unstable network mode (user sees blank photo placeholder)
                        const photoArray = response.filter((responseObject) => {
                            foundError = responseObject.error && responseObject.url === undefined;
                            return !foundError;
                        });
                        // update
                        const copyPhotos = [].concat(this.state.photos, photoArray);
                        this.props.updateFormData({ photos: copyPhotos });
                        this.setState({ uploadingImages: false });
                        this.props.updateStatusMessage('Upload complete!', NotificationConstants.SUCCESS);
                        this.props.updateListingPhotoProcessingStatus(false);

                        // handle error
                        // if error is found here
                        // implies that it was not max file limit error. set the isfileSizeOutOfLimit false if previously set true
                        if (foundError) {
                            this.setState({
                                isfileSizeOutOfLimit: false,
                            });
                            throw Error;
                        }

                        if (hasInvalidExtension) {
                            this.handleInvalidExtension();
                        }
                    })
                    .catch(() => {
                        const clientErrMsg = this.state.isfileSizeOutOfLimit
                            ? 'Please upload valid photos less than 25MB in size'
                            : ErrorMessageConstants.DEFAULT_PHOTO_ERROR;

                        this.setState({ uploadingImages: false });

                        this.props.updateErrorObj({ photos: clientErrMsg });

                        this.props.updateListingPhotoProcessingStatus(false);
                        this.props.updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);

                        appLogger.logError({
                            message: clientErrMsg,
                            context: 'PhotoUpload.onDrop',
                        });
                    });
            } else {
                this.setState({
                    uploadingImages: false,
                    dropzoneStatusMessage: '',
                });

                if (hasInvalidExtension) {
                    this.handleInvalidExtension();
                }
            }
        });
    }

    onDropRejected() {
        this.setState({
            uploadingImages: false,
            isfileSizeOutOfLimit: true,
        });
        const clientErrMsg = 'Please upload valid photos less than 25MB in size.';
        this.props.updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);
        this.props.updateErrorObj({ photos: true });
    }

    saveSortedPhotos() {
        const { formData, updateStatusMessage } = this.props;
        const { photos, listingAlias } = formData;

        this.props.updateListingPhotoProcessingStatus(true);
        updateStatusMessage('Saving...');

        // populate photos list
        const photoArrayToSave = [];
        photos.forEach((photo, index) => {
            const photoObj = {
                id: photo.id,
                rank: index + 1,
            };

            photoArrayToSave.push(photoObj);
        });

        return this.props
            .dispatch(PostingActions.property.photo.sort({
                propertyId: listingAlias,
                photosArray: photoArrayToSave,
            }))
            .then(() => {
                updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);
                this.props.updateListingPhotoProcessingStatus(false);
            })
            .catch((errorRMWModel) => {
                this.props.updateStatusMessage(MISMATCH_PHOTOS_ERROR, NotificationConstants.ERROR);
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PhotoUpload.saveSortedPhotos',
                });
            });
    }

    removePhoto(index) {
        if (this.state.isListingPhotoProcessing) {
            console.warn('[WARNING] listing photo is currently rotating');
            return;
        }

        const photosArray = [].concat(this.state.photos);
        const photoToRemove = photosArray[index];
        this.props.updateStatusMessage('Saving...');

        this.props.updateListingPhotoProcessingStatus(true);
        this.props.dispatch(PostingActions.property.photo.delete(photoToRemove.id))
            .then(() => {
                photosArray.splice(index, 1);
                this.props.updateFormData({ photos: photosArray });
                this.props.updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);
                this.props.updateListingPhotoProcessingStatus(false);
            }).catch((errorRMWModel) => {
                let errorMessage = 'There was an error deleting the photo. Please, try again.';
                if (errorRMWModel.message === ErrorMessageConstants.DELETE_LAST_PHOTO_ACTIVE_LISTING_ERROR) {
                    errorMessage = errorRMWModel.message;
                }
                this.props.updateStatusMessage(errorMessage, NotificationConstants.ERROR);
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PhotoUpload.removePhoto',
                });
            });
    }

    savePhotoCaption(photoId, caption) {
        this.props.updateStatusMessage('Saving...');
        this.props.updateListingPhotoProcessingStatus(true);

        return this.props.dispatch(
            PostingActions.property.photo.caption(photoId, caption)
        )
            .then(() => {
                this.deleteIfCaptionErrorExistsAndDisplayNextError(photoId);

                this.props.updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);
                this.props.updateListingPhotoProcessingStatus(false);
            })
            .catch((errorRMWModel) => {
                const errorMessage = errorRMWModel.message;
                this.addAndDisplayCaptionError(photoId, errorMessage);

                const friendlyMessage = 'Please fix the the error in the photo captions.';
                this.props.updateStatusMessage(friendlyMessage, NotificationConstants.ERROR);
                this.props.updateListingPhotoProcessingStatus(false);
            });
    }

    shouldCancelSortableListStart() {
        return this.state.isListingPhotoProcessing;
    }

    updateCaption(input = '', index) {
        const photosArray = [].concat(this.state.photos);
        photosArray[index].caption = input;

        this.props.updateFormData({
            photos: photosArray,
        });
    }

    uploadPhoto(fileOrBlob) {
        const formData = new FormData();
        formData.append('sourceFile', fileOrBlob);

        const dataForApi = {
            fileOrBlob,
            formData,
            propertyId: this.props.formData.listingAlias,
        };
        return this.props
            .dispatch(PostingActions.property.photo.upload(dataForApi))
            .then((result) => {
                this.props.updateErrorObj({ photos: false });
                return result;
            })
            .catch((clientErrMsg) => {
                this.setState({ uploadingImages: false });
                this.props.updateStatusMessage(
                    ErrorMessageConstants.DEFAULT_PHOTO_ERROR,
                    NotificationConstants.ERROR
                );
                this.props.updateErrorObj({ photos: clientErrMsg });

                appLogger.logError({
                    message: clientErrMsg,
                    context: 'uploadPhoto',
                });

                return false;
            });
    }

    onSortEnd({ oldIndex, newIndex }) {
        const { photos } = this.state;
        let updatedPhotoArray = [].concat(photos);
        updatedPhotoArray = arrayMove(updatedPhotoArray, oldIndex, newIndex);

        // form data photos is not getting updated correctly on next and prev action
        this.props.updateFormData({
            photos: updatedPhotoArray,
        });

        // on callback update state with value from updatedPhotoArray
        this.setState({ photos: updatedPhotoArray }, () => {
            // sync caption and save
            const oldPhoto = updatedPhotoArray[oldIndex];
            const oldPhotoCaption = oldPhoto.caption || '';

            this.updateCaption(oldPhotoCaption, oldIndex);
            this.saveSortedPhotos();
        });
    }

    /**
     * 1) Create new rotated image File
     * 2) Replace rotated photo
     * 3) Update photos in form data with response
     */
    rotatePhoto(photoIndex) {
        if (this.state.isListingPhotoProcessing) {
            console.warn('[WARNING] listing photo is currently rotating');
            return Promise.reject();
        }

        const {
            formData,
            updateFormData,
            updateErrorObj,
            updateStatusMessage,
            updateListingPhotoProcessingStatus,
        } = this.props;

        const photosCopy = [].concat(formData.photos);
        const photoToRotate = photosCopy[photoIndex];

        // start rotate photo
        updateErrorObj({ photos: null });
        updateStatusMessage('Rotating image...');
        updateListingPhotoProcessingStatus(true);

        return this.props
            .dispatch(PostingActions.property.photo.rotate(photoToRotate.id, true))
            .then((response) => {
                const rotatedPhotoUrl = response.response.url;
                photoToRotate.url = rotatedPhotoUrl;

                updateFormData({ photos: photosCopy });
                updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);
                updateListingPhotoProcessingStatus(false);
            })
            .catch((errorRMWModel) => {
                // error rotate photo
                updateErrorObj({ photos: errorRMWModel.message });
                updateStatusMessage('');
                updateListingPhotoProcessingStatus(false);
            });
    }

    renderDropZone() {
        const { uploadingImages, dropzoneStatusMessage, showInvalidFiletypeText } = this.state;
        const { hasError, statusMessage } = this.props;

        return (
            <PhotoUploadDropzoneContainer className="PhotoUpload-dropzone-container">
                <DropzoneWrapper
                    isUploading={uploadingImages}
                    isUploadingCaption={dropzoneStatusMessage}
                    onDrop={this.onDrop}
                    onDropRejected={this.onDropRejected}
                    includeMaxSize={true}
                    multiple={true}
                />

                {
                    showInvalidFiletypeText &&
                    <InvalidFiletypeText containsMultiple={true} />
                }

                {hasError && !uploadingImages && (
                    <FormHelp
                        error={true}
                        marginTop={0.5}
                    >
                        {statusMessage}
                    </FormHelp>
                )}
            </PhotoUploadDropzoneContainer>
        );
    }

    renderUploadedPhotos() {
        const { isListingPhotoProcessing, photos } = this.state;
        const { isScreenMobile, isInputFormDisabled = false, errorObj } = this.props;

        const { photoCaptions } = errorObj;
        let errorMsg;
        if (photoCaptions) {
            const firstBadCaptionPhotoId = photoCaptions.keys().next().value;
            errorMsg = photoCaptions.get(firstBadCaptionPhotoId);
        }

        return (
            <Spacer marginBottom="md">
                {this.state.photos.length && (
                    <ThumbList
                        isMobileScreenResolution={isScreenMobile}
                        isListingPhotoProcessing={isListingPhotoProcessing}
                        list={photos}
                        // actions
                        handleItemClick={this.handleItemClick}
                        onSortEnd={this.onSortEnd}
                        removePhoto={this.removePhoto}
                        rotatePhoto={this.rotatePhoto}
                        isDisabled={isInputFormDisabled}
                    />
                )}
                {errorMsg && (
                    <FormHelp
                        error={!!errorMsg}
                        marginTop={0.5}
                        dangerouslySetInnerHTML={{ __html: errorMsg }}
                    />
                )}
            </Spacer>
        );
    }

    render() {
        const {
            currentSlideIndex, isListingPhotoProcessing, photos, startSlideIndex, statusMessage,
        } = this.state;

        const {
            modalImageCarousel, isInputFormDisabled = false, showHeading, errorObj,
        } = this.props;

        const hasPhotos = photos.length > 0;

        return (
            <StyledPhotoUpload className="PhotoUpload">
                {showHeading && (
                    <Text
                        marginBottom="xs"
                        mediaQuery={{ lg: { marginBottom: 'sm' } }}
                        fontType="h4"
                        as="div"
                    >
                        Photos
                    </Text>
                )}

                <StyledHiddenAnchor id="photos" />

                <Row className="EditListingPage-content-full photo-content">
                    <StyledCol marginBottom={-1} className="photos">
                        {hasPhotos && this.renderUploadedPhotos()}

                        {!isInputFormDisabled && this.renderDropZone()}
                    </StyledCol>
                </Row>

                {hasPhotos && (
                    <div data-test="ImageCarouselModal">
                        <ImageCarouselModal
                            backToOrigPos={true}
                            currentSlideIndex={currentSlideIndex}
                            isOpen={modalImageCarousel.isOpen}
                            isListingPhotoProcessing={isListingPhotoProcessing}
                            list={photos}
                            startSlideIndex={startSlideIndex}
                            statusMessage={statusMessage}
                            rotatePhoto={this.rotatePhoto}
                            savePhotoCaption={this.savePhotoCaption}
                            updateCaption={this.updateCaption}
                            updateCurrentSlideIndex={this.updateCurrentSlideIndex}
                            isDisabled={isInputFormDisabled}
                            errorObj={errorObj}
                        />
                    </div>
                )}
            </StyledPhotoUpload>
        );
    }
}

const mapStateToProps = state => ({
    modalImageCarousel: ModalSelectors.makeImageCarouselModal(state),
    user: UserSelectors.makeUser(state),
});

export { PhotoUpload };
export default connect(mapStateToProps)(PhotoUpload);
